"use client";

import React, { useMemo, useState, useEffect } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  LineChart,
  BarChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ReferenceArea,
  Brush,
} from "recharts";
import { Card, Kpi, SectionTitle } from "../ui";
import { FilterChip } from "../charts";
import { HealthDay, LoadRow } from "@/lib/types";
import { useUI, Palette } from "@/lib/theme";
import { fmtDateWeekday, fmtNum } from "@/lib/format";

export type HealthView = "s_resumen" | "s_sueno" | "s_cuerpo" | "s_cruce";

/**
 * Paleta de salud, elegida para que las series que conviven en un mismo gráfico
 * se distingan bien (p. ej. readiness=violeta vs sueño=cian, antes morado/azul).
 */
const C = {
  sleep: "#06b6d4", // cian (score de sueño)
  readiness: "#8b5cf6", // violeta
  hrv: "#10b981", // esmeralda
  rhr: "#ef4444", // rojo
  stress: "#f59e0b", // ámbar
  bbHigh: "#14b8a6", // teal
  bbLow: "#9a958a", // warm muted
  vo2: "#fc4c02", // naranja (marca)
  steps: "#0ea5e9", // azul cielo
  tsb: "#3b82f6", // azul (convención PMC)
  // Fases de sueño: rampa fría que no choca con el cian del score.
  deep: "#3730a3", // indigo-800
  light: "#818cf8", // indigo-400
  rem: "#c084fc", // purple-400
  awake: "#e6e1d8", // warm light (--line claro)
};

/**
 * Clasificación de VO2máx por sexo y edad (ACSM / Cooper Institute, ml/kg/min).
 * `cuts` = los 5 cortes que separan las 6 categorías: Deficiente < c0 ≤ Por debajo <
 * c1 ≤ Promedio < c2 ≤ Por encima < c3 ≤ Bueno < c4 ≤ Excelente.
 */
const VO2_TABLE: Record<"MALE" | "FEMALE", { maxAge: number; cuts: number[] }[]> = {
  MALE: [
    { maxAge: 29, cuts: [33, 37, 42, 46, 53] },
    { maxAge: 39, cuts: [31, 35, 40, 44, 51] },
    { maxAge: 49, cuts: [28, 33, 37, 41, 48] },
    { maxAge: 59, cuts: [25, 29, 34, 38, 45] },
    { maxAge: 69, cuts: [22, 26, 31, 35, 42] },
    { maxAge: 200, cuts: [19, 23, 28, 32, 39] },
  ],
  FEMALE: [
    { maxAge: 29, cuts: [24, 29, 33, 37, 42] },
    { maxAge: 39, cuts: [22, 27, 31, 35, 40] },
    { maxAge: 49, cuts: [20, 25, 29, 33, 38] },
    { maxAge: 59, cuts: [18, 22, 26, 30, 35] },
    { maxAge: 69, cuts: [16, 19, 23, 27, 32] },
    { maxAge: 200, cuts: [14, 17, 21, 25, 30] },
  ],
};
const VO2_NAMES = ["vo2_1", "vo2_2", "vo2_3", "vo2_4", "vo2_5", "vo2_6"];
const VO2_COLORS = ["#ef4444", "#f97316", "#eab308", "#84cc16", "#22c55e", "#14b8a6"];

function vo2Cuts(sex?: string, age?: number): number[] | null {
  if (!sex || age == null) return null;
  const u = sex.toUpperCase();
  const s = u === "FEMALE" ? "FEMALE" : u === "MALE" ? "MALE" : null;
  if (!s) return null;
  const t = VO2_TABLE[s];
  return (t.find((r) => age <= r.maxAge) ?? t[t.length - 1]).cuts;
}
function vo2CatIdx(v: number, cuts: number[]): number {
  let idx = 0;
  for (let i = 0; i < cuts.length; i++) if (v >= cuts[i]) idx = i + 1;
  return idx; // 0..5
}

const MES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/* ---------- helpers ---------- */
function lastVal(rows: HealthDay[], k: keyof HealthDay): number | null {
  for (let i = rows.length - 1; i >= 0; i--) {
    const v = rows[i][k];
    if (typeof v === "number") return v;
  }
  return null;
}
function firstVal(rows: HealthDay[], k: keyof HealthDay): number | null {
  for (let i = 0; i < rows.length; i++) {
    const v = rows[i][k];
    if (typeof v === "number") return v;
  }
  return null;
}
/** Variación de una métrica puntual (último − primero) a lo largo del periodo: flecha de
 *  dirección + magnitud + "en N días" (mismo formato claro que la carga). */
function deltaSub(
  rows: HealthDay[],
  k: keyof HealthDay,
  loc: string,
  t: (k: string, p?: Record<string, string | number>) => string,
  decimals = 0,
): string | undefined {
  const a = firstVal(rows, k);
  const b = lastVal(rows, k);
  if (a == null || b == null || rows.length < 2) return undefined;
  const d = b - a;
  const days = Math.round(
    (new Date(rows[rows.length - 1].date + "T00:00:00").getTime() -
      new Date(rows[0].date + "T00:00:00").getTime()) /
      86400000,
  );
  const arrow = d >= 0 ? "↑" : "↓";
  const mag = `${arrow} ${fmtNum(Math.abs(d), decimals, loc)}`;
  return days > 0 ? `${mag} ${t("kpi_over_days", { d: days })}` : mag;
}
function mean(xs: number[]): number | null {
  const v = xs.filter((x) => typeof x === "number" && !Number.isNaN(x));
  return v.length ? v.reduce((s, x) => s + x, 0) / v.length : null;
}
/** Correlación de Pearson sobre los pares donde ambos valores existen. */
function pearson(pairs: [number | null, number | null][]): number | null {
  const ok = pairs.filter((p): p is [number, number] => typeof p[0] === "number" && typeof p[1] === "number");
  if (ok.length < 8) return null;
  const xs = ok.map((p) => p[0]);
  const ys = ok.map((p) => p[1]);
  const mx = mean(xs)!;
  const my = mean(ys)!;
  let num = 0,
    dx = 0,
    dy = 0;
  for (let i = 0; i < ok.length; i++) {
    const a = xs[i] - mx,
      b = ys[i] - my;
    num += a * b;
    dx += a * a;
    dy += b * b;
  }
  const den = Math.sqrt(dx * dy);
  return den === 0 ? null : num / den;
}

function tipProps(p: Palette) {
  return {
    contentStyle: {
      background: p.tipBg,
      border: `1px solid ${p.tipBorder}`,
      borderRadius: 12,
      color: p.tipText,
      fontSize: 12,
    },
    labelStyle: { color: p.tipLabel },
    itemStyle: { color: p.tipText },
  };
}

/** Formatos de fecha: `short` para el eje ("12 jun") y `full` con año para tooltips ("12 jun 2024"). */
function useDateFmt() {
  const { lang } = useUI();
  const arr = lang === "en" ? MON : MES;
  const short = (iso: string) => {
    const d = new Date(iso + "T00:00:00");
    return `${d.getDate()} ${arr[d.getMonth()]}`;
  };
  const full = (iso: string) => {
    const d = new Date(iso + "T00:00:00");
    return `${d.getDate()} ${arr[d.getMonth()]} ${d.getFullYear()}`;
  };
  return { short, full };
}

/* ---------- KPIs (con tooltips informativos) ---------- */
function HealthKpis({ rows }: { rows: HealthDay[] }) {
  const { t, loc } = useUI();
  const num = (v: number | null, d = 0, suf = "") => (v == null ? "—" : `${fmtNum(v, d, loc)}${suf}`);
  const vals = (k: keyof HealthDay) =>
    rows.map((r) => r[k]).filter((x): x is number => typeof x === "number");
  const avg = (k: keyof HealthDay) => mean(vals(k));
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted">{t("h_kpis_caption")}</p>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        <Kpi label={t("h_steps")} value={num(avg("steps"))} accent />
        <Kpi label={t("health_distance")} value={avg("distance_m") == null ? "—" : `${fmtNum((avg("distance_m") ?? 0) / 1000, 1, loc)} km`} accent />
        <Kpi label={t("h_calories")} value={num(avg("calories"), 0, " kcal")} accent info={t("info_h_cal")} />
        <Kpi label={t("h_sleep_score")} value={num(avg("sleep_score"))} accent info={t("info_h_sleep")} />
        <Kpi label={t("h_stress")} value={num(avg("stress_avg"))} accent info={t("info_h_stress")} />
        <Kpi label={t("h_avg_hr")} value={num(avg("avg_hr"), 0, " ppm")} accent info={t("info_h_avghr")} />
      </div>
    </div>
  );
}

/* ---------- Tabla de resumen diario (una fila por día, estilo Strava) ---------- */
type HCol = {
  key: string;
  label: string;
  right?: boolean;
  val: (h: HealthDay) => number | null;
  cell?: (h: HealthDay) => React.ReactNode;
};
const HPAGE_SIZES = [10, 25, 50, 75, 100];

/** Orden de columnas por defecto (tabla de Sueño). */
const HCOLS_DEFAULT = [
  "sleep_score",
  "sleep_dur",
  "hrv_avg",
  "resting_hr",
  "stress_avg",
  "bb_high",
  "steps",
  "readiness",
  "vo2max",
];
/** Columnas de la tabla de Resumen y Cuerpo (sin HRV/FC reposo; con calorías y FC media del día). */
const HCOLS_SUMMARY = [
  "sleep_score",
  "sleep_dur",
  "stress_avg",
  "bb_high",
  "steps",
  "calories",
  "avg_hr",
  "readiness",
  "vo2max",
];

function HealthTable({ rows, only }: { rows: HealthDay[]; only?: string[] }) {
  const { t, loc } = useUI();
  const [sortKey, setSortKey] = useState("date");
  const [dir, setDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const n1 = (v: number | null, d = 0) => (v == null ? "—" : fmtNum(v, d, loc));
  const allCols: HCol[] = [
    { key: "sleep_score", label: t("h_sleep_score"), right: true, val: (h) => h.sleep_score },
    {
      key: "sleep_dur",
      label: t("h_sleep_dur"),
      right: true,
      val: (h) => h.sleep_seconds,
      cell: (h) => (h.sleep_seconds == null ? "—" : `${fmtNum(h.sleep_seconds / 3600, 1, loc)} h`),
    },
    { key: "hrv_avg", label: t("h_hrv"), right: true, val: (h) => h.hrv_avg },
    { key: "resting_hr", label: t("h_rhr"), right: true, val: (h) => h.resting_hr },
    { key: "stress_avg", label: t("h_stress"), right: true, val: (h) => h.stress_avg },
    {
      key: "bb_high",
      label: t("h_body_battery"),
      right: true,
      val: (h) => h.bb_high,
      cell: (h) => (h.bb_high == null && h.bb_low == null ? "—" : `${n1(h.bb_high)}/${n1(h.bb_low)}`),
    },
    { key: "steps", label: t("h_steps"), right: true, val: (h) => h.steps },
    {
      key: "calories",
      label: t("h_calories"),
      right: true,
      val: (h) => h.calories,
      cell: (h) => (h.calories == null ? "—" : `${fmtNum(h.calories, 0, loc)}`),
    },
    { key: "avg_hr", label: t("h_avg_hr"), right: true, val: (h) => h.avg_hr },
    { key: "readiness", label: t("h_readiness"), right: true, val: (h) => h.readiness },
    { key: "vo2max", label: t("h_vo2"), right: true, val: (h) => h.vo2max, cell: (h) => n1(h.vo2max, 1) },
  ];
  const cols: HCol[] = (only ?? HCOLS_DEFAULT)
    .map((k) => allCols.find((c) => c.key === k))
    .filter((c): c is HCol => !!c);

  const sorted = useMemo(() => {
    const arr = [...rows];
    const f =
      sortKey === "date"
        ? (h: HealthDay) => h.date
        : (cols.find((c) => c.key === sortKey)?.val ?? (() => null));
    arr.sort((a, b) => {
      const va = f(a) as number | string | null,
        vb = f(b) as number | string | null;
      if (va == null && vb == null) return 0;
      if (va == null) return 1; // nulos al final
      if (vb == null) return -1;
      if (va < vb) return dir === "asc" ? -1 : 1;
      if (va > vb) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [rows, sortKey, dir]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const cur = Math.min(page, totalPages - 1);
  const pageRows = sorted.slice(cur * pageSize, cur * pageSize + pageSize);
  const from = cur * pageSize + 1;
  const to = Math.min(sorted.length, (cur + 1) * pageSize);
  const click = (k: string) => {
    setSortKey(k);
    setDir((d) => (sortKey === k ? (d === "asc" ? "desc" : "asc") : "desc"));
    setPage(0);
  };

  if (rows.length === 0) return <p className="py-4 text-sm text-muted">{t("h_no_data")}</p>;

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-muted">
            <tr className="border-b border-line">
              {[{ key: "date", label: t("th_fecha"), right: false }, ...cols].map((c) => (
                <th
                  key={c.key}
                  onClick={() => click(c.key)}
                  className={`cursor-pointer select-none whitespace-nowrap py-2 pr-4 font-medium hover:text-fg ${(c as HCol).right ? "text-right" : ""}`}
                >
                  {c.label}
                  <span className="ml-1 text-[10px]">
                    {sortKey === c.key ? (dir === "asc" ? "▲" : "▼") : "↕"}
                  </span>
                </th>
              ))}
              <th className="whitespace-nowrap py-2 pl-2 text-center font-medium">{t("th_garmin")}</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((h) => (
              <tr key={h.date} className="border-b border-line/50 transition hover:bg-muted/10">
                <td className="whitespace-nowrap py-2 pr-4 text-muted">{fmtDateWeekday(h.date, loc)}</td>
                {cols.map((c) => (
                  <td key={c.key} className={`py-2 pr-4 ${c.right ? "text-right" : ""}`}>
                    {c.cell ? c.cell(h) : n1(c.val(h))}
                  </td>
                ))}
                <td className="py-2 pl-2 text-center">
                  <a
                    href={`https://connect.garmin.com/modern/daily-summary/${h.date}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={t("h_garmin_link")}
                    aria-label={t("h_garmin_link")}
                    className="inline-flex items-center justify-center text-muted transition hover:text-accent"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <path d="M14 3h7v7M21 3l-9 9M19 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" />
                    </svg>
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-muted">
        <div className="flex items-center gap-2">
          <span>{t("pg_show")}</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
            className="rounded-lg border border-line bg-panel px-2 py-1 text-fg outline-none focus:border-accent"
          >
            {HPAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <span>
            {t("pg_of_total", {
              from: fmtNum(from, 0, loc),
              to: fmtNum(to, 0, loc),
              total: fmtNum(sorted.length, 0, loc),
            })}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage(cur - 1)}
            disabled={cur === 0}
            className="rounded-lg border border-line px-3 py-1 font-medium transition enabled:hover:text-fg disabled:opacity-40"
          >
            ‹ {t("pg_prev")}
          </button>
          <span className="px-2">{t("pg_page", { page: cur + 1, total: totalPages })}</span>
          <button
            onClick={() => setPage(cur + 1)}
            disabled={cur >= totalPages - 1}
            className="rounded-lg border border-line px-3 py-1 font-medium transition enabled:hover:text-fg disabled:opacity-40"
          >
            {t("pg_next")} ›
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- FC intradía (un día; arrastra un área para hacer zoom, clic para resetear) ---------- */
function HrIntradayCard({ days }: { days: { date: string; pts: [number, number][] }[] }) {
  const { t, loc, palette } = useUI();
  const sorted = useMemo(() => [...days].sort((a, b) => b.date.localeCompare(a.date)), [days]);
  const [sel, setSel] = useState(sorted[0]?.date ?? "");
  const day = sorted.find((d) => d.date === sel) ?? sorted[0];
  const data = (day?.pts ?? []).map(([m, hr]) => ({ m, hr }));
  const hhmm = (m: number) =>
    `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  const dlabel = (iso: string) =>
    new Date(iso + "T00:00:00").toLocaleDateString(loc, { weekday: "short", day: "2-digit", month: "short" });

  // Zoom por arrastre: la ventana visible (índices del Brush) se fija arrastrando un área
  // sobre el gráfico; un clic simple la resetea. El Brush queda como control fino.
  const [win, setWin] = useState<{ s: number; e: number } | null>(null);
  const [drag, setDrag] = useState<{ a: number; b: number } | null>(null);
  useEffect(() => {
    setWin(null);
    setDrag(null);
  }, [sel]);
  const finalizeDrag = () =>
    setDrag((d) => {
      if (d) {
        if (d.a !== d.b) {
          const lo = Math.min(d.a, d.b),
            hi = Math.max(d.a, d.b);
          const s = data.findIndex((p) => p.m >= lo);
          let e = data.findIndex((p) => p.m >= hi);
          if (e < 0) e = data.length - 1;
          if (s >= 0 && e > s) setWin({ s, e });
        } else {
          setWin(null); // clic = quitar zoom
        }
      }
      return null;
    });
  const dragMouse = {
    onMouseDown: (st: any) => {
      if (st?.activeLabel != null) setDrag({ a: Number(st.activeLabel), b: Number(st.activeLabel) });
    },
    onMouseMove: (st: any) => {
      if (st?.activeLabel != null) setDrag((d) => (d ? { ...d, b: Number(st.activeLabel) } : d));
    },
    onMouseUp: finalizeDrag,
    onMouseLeave: finalizeDrag,
  };

  return (
    <Card>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <SectionTitle>{t("sec_h_hr_day")}</SectionTitle>
        <label className="flex items-center gap-2 text-xs text-muted">
          {t("h_day")}
          <select
            value={sel}
            onChange={(e) => setSel(e.target.value)}
            className="rounded-lg border border-line bg-panel px-2 py-1 text-fg outline-none focus:border-accent"
          >
            {sorted.map((d) => (
              <option key={d.date} value={d.date}>
                {dlabel(d.date)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={data} className="cursor-crosshair select-none" {...dragMouse}>
          <CartesianGrid stroke={palette.grid} strokeOpacity={0.5} vertical={false} />
          <XAxis dataKey="m" tickFormatter={hhmm} stroke={palette.axis} fontSize={12} minTickGap={48} />
          <YAxis stroke={palette.axis} fontSize={12} />
          <Tooltip
            {...tipProps(palette)}
            labelFormatter={(m) => hhmm(Number(m))}
            formatter={(v: number) => [`${v} ppm`, t("h_hr_bpm")]}
          />
          {drag && drag.a !== drag.b && (
            <ReferenceArea
              x1={Math.min(drag.a, drag.b)}
              x2={Math.max(drag.a, drag.b)}
              fill={C.rhr}
              fillOpacity={0.12}
              stroke={C.rhr}
              strokeOpacity={0.3}
            />
          )}
          <Line
            type="monotone"
            dataKey="hr"
            name={t("h_hr_bpm")}
            stroke={C.rhr}
            dot={false}
            strokeWidth={1.5}
          />
          <Brush
            dataKey="m"
            height={22}
            stroke={C.rhr}
            fill={palette.tipBg}
            travellerWidth={8}
            tickFormatter={(m) => hhmm(Number(m))}
            onChange={(e: any) =>
              setWin(
                e?.startIndex === 0 && e?.endIndex === data.length - 1
                  ? null
                  : { s: e.startIndex, e: e.endIndex },
              )
            }
            {...(win ? { startIndex: win.s, endIndex: win.e } : {})}
          />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}

/* ---------- vistas ---------- */
export default function Health({
  view,
  health,
  load,
  hrIntraday,
  athlete,
}: {
  view: HealthView;
  health: HealthDay[];
  load: LoadRow[];
  hrIntraday: { date: string; pts: [number, number][] }[];
  athlete?: { sex?: string; age?: number };
}) {
  const { t, loc, palette } = useUI();
  const { short: dfmt, full: dfull } = useDateFmt();
  const rows = health;

  if (!rows.length) {
    return (
      <Card>
        <p className="py-8 text-center text-sm text-muted">{t("h_no_data")}</p>
      </Card>
    );
  }

  const axis = { stroke: palette.axis, fontSize: 12 };
  const grid = <CartesianGrid stroke={palette.grid} strokeOpacity={0.5} vertical={false} />;

  // Filtro dinámico por brush: seleccionar un área en cualquier gráfica filtra toda
  // la tab (KPIs, tabla y demás gráficas) a ese rango de fechas, para ganar detalle.
  const [sel, setSel] = useState<{ start: number; end: number } | null>(null);
  useEffect(() => setSel(null), [health, view]);
  const range = sel ? rows.slice(sel.start, sel.end + 1) : rows;
  const onBrush = (e: any) => {
    if (!e || typeof e.startIndex !== "number" || typeof e.endIndex !== "number") return;
    const full = e.startIndex === 0 && e.endIndex === rows.length - 1;
    setSel((cur) => {
      const next = full ? null : { start: e.startIndex, end: e.endIndex };
      if (cur === next) return cur;
      if (cur && next && cur.start === next.start && cur.end === next.end) return cur;
      return next;
    });
  };
  const mkBrush = () => (
    <Brush
      dataKey="date"
      height={16}
      stroke={palette.axis}
      fill={palette.tipBg}
      travellerWidth={8}
      tickFormatter={dfmt}
      onChange={onBrush}
      {...(sel && sel.end > sel.start ? { startIndex: sel.start, endIndex: sel.end } : {})}
    />
  );
  const clearChip =
    sel && range.length ? (
      <div className="flex">
        <FilterChip
          color={C.readiness}
          label={
            range.length === 1
              ? dfull(range[0].date)
              : `${dfull(range[0].date)} – ${dfull(range[range.length - 1].date)}`
          }
          onClear={() => setSel(null)}
        />
      </div>
    ) : null;

  // Selección por ARRASTRE del ratón sobre el área del chart (además del brush inferior).
  const dateIndex = useMemo(() => {
    const m = new Map<string, number>();
    rows.forEach((r, i) => m.set(r.date, i));
    return m;
  }, [rows]);
  const [drag, setDrag] = useState<{ a: string; b: string } | null>(null);
  const finalizeDrag = () => {
    setDrag((d) => {
      if (d) {
        const i = dateIndex.get(d.a),
          j = dateIndex.get(d.b);
        // i === j => clic en un solo día (filtra la tabla/KPIs a ese día).
        if (i != null && j != null) setSel({ start: Math.min(i, j), end: Math.max(i, j) });
      }
      return null;
    });
  };
  const mouseEvents = {
    onMouseDown: (e: any) => {
      if (e?.activeLabel) setDrag({ a: e.activeLabel, b: e.activeLabel });
    },
    onMouseMove: (e: any) => {
      if (e?.activeLabel) setDrag((d) => (d ? { ...d, b: e.activeLabel } : d));
    },
    onMouseUp: finalizeDrag,
    onMouseLeave: finalizeDrag,
  };
  const mkBand = (yAxisId?: string) =>
    drag && drag.a !== drag.b ? (
      <ReferenceArea
        {...(yAxisId ? { yAxisId } : {})}
        x1={drag.a}
        x2={drag.b}
        fill={palette.cursor}
        fillOpacity={0.12}
        stroke={palette.cursor}
        strokeOpacity={0.3}
      />
    ) : null;

  /* ---- RESUMEN ---- */
  if (view === "s_resumen") {
    return (
      <div className="space-y-6">
        {clearChip}
        <HealthKpis rows={range} />
        <Card>
          <SectionTitle>{t("sec_h_activity")}</SectionTitle>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={rows} {...mouseEvents}>
              {grid}
              {mkBand("steps")}
              <XAxis dataKey="date" tickFormatter={dfmt} {...axis} minTickGap={40} />
              <YAxis yAxisId="steps" {...axis} />
              <YAxis yAxisId="cal" orientation="right" {...axis} />
              <Tooltip {...tipProps(palette)} labelFormatter={dfull} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="steps" dataKey="steps" name={t("h_steps")} fill={C.steps} radius={[2, 2, 0, 0]} />
              <Line
                yAxisId="cal"
                type="monotone"
                dataKey="calories"
                name={t("h_calories")}
                stroke={C.vo2}
                dot={false}
                strokeWidth={2}
                connectNulls
              />
              {mkBrush()}
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <SectionTitle>{t("sec_h_sleepstress")}</SectionTitle>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={rows} {...mouseEvents}>
              {grid}
              {mkBand()}
              <XAxis dataKey="date" tickFormatter={dfmt} {...axis} minTickGap={40} />
              <YAxis domain={[0, 100]} {...axis} />
              <Tooltip {...tipProps(palette)} labelFormatter={dfull} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line
                type="monotone"
                dataKey="sleep_score"
                name={t("h_sleep_score")}
                stroke={C.sleep}
                dot={false}
                strokeWidth={2.5}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="stress_avg"
                name={t("h_stress")}
                stroke={C.stress}
                dot={false}
                strokeWidth={2}
                connectNulls
              />
              {mkBrush()}
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <SectionTitle>{t("sec_h_avghr")}</SectionTitle>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={rows} {...mouseEvents}>
              {grid}
              {mkBand()}
              <XAxis dataKey="date" tickFormatter={dfmt} {...axis} minTickGap={40} />
              <YAxis domain={["dataMin - 3", "dataMax + 3"]} {...axis} />
              <Tooltip {...tipProps(palette)} labelFormatter={dfull} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line
                type="monotone"
                dataKey="avg_hr"
                name={t("h_avg_hr")}
                stroke={C.rhr}
                dot={false}
                strokeWidth={2}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="resting_hr"
                name={t("h_rhr")}
                stroke="#3b82f6"
                dot={false}
                strokeWidth={2}
                connectNulls
              />
              {mkBrush()}
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <SectionTitle>{t("sec_h_table")}</SectionTitle>
          <HealthTable rows={range} only={HCOLS_SUMMARY} />
        </Card>
      </div>
    );
  }

  /* ---- CUERPO ---- */
  if (view === "s_cuerpo") {
    const cuVals = (k: keyof HealthDay) =>
      range.map((r) => r[k]).filter((x): x is number => typeof x === "number");
    const num = (v: number | null, d = 0, suf = "") => (v == null ? "—" : `${fmtNum(v, d, loc)}${suf}`);
    const avgSteps = mean(cuVals("steps"));
    const avgHr = mean(cuVals("avg_hr"));
    const vo2cur = lastVal(rows, "vo2max"); // "actual" = último disponible
    const vo2arr = rows.map((r) => r.vo2max).filter((x): x is number => typeof x === "number");
    const vmin = vo2arr.length ? Math.min(...vo2arr) : 0;
    const vmax = vo2arr.length ? Math.max(...vo2arr) : 0;
    const cuts = vo2Cuts(athlete?.sex, athlete?.age);
    const vo2cat = vo2cur != null && cuts ? t(VO2_NAMES[vo2CatIdx(vo2cur, cuts)]) : null;
    const vo2sub = [vo2cat, deltaSub(rows, "vo2max", loc, t, 1)].filter(Boolean).join(" · ") || undefined;
    return (
      <div className="space-y-6">
        {clearChip}
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted">{t("h_kpis_caption")}</p>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            <Kpi label={t("h_steps")} value={num(avgSteps)} accent />
            <Kpi label={t("h_avg_hr")} value={num(avgHr, 0, " ppm")} accent info={t("info_h_avghr")} />
            <Kpi
              label={t("h_vo2")}
              value={vo2cur == null ? "—" : fmtNum(vo2cur, 1, loc)}
              accent
              sub={vo2sub}
              info={t("info_h_vo2")}
            />
          </div>
        </div>
        <Card>
          <SectionTitle>{t("sec_h_steps")}</SectionTitle>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={rows} {...mouseEvents}>
              {grid}
              {mkBand()}
              <XAxis dataKey="date" tickFormatter={dfmt} {...axis} minTickGap={40} />
              <YAxis {...axis} />
              <Tooltip {...tipProps(palette)} labelFormatter={dfull} />
              <Bar dataKey="steps" name={t("h_steps")} fill={C.steps} radius={[2, 2, 0, 0]} />
              {mkBrush()}
            </BarChart>
          </ResponsiveContainer>
        </Card>
        {hrIntraday.length > 0 && <HrIntradayCard days={hrIntraday} />}
        <Card>
          <SectionTitle>
            {t("sec_h_vo2")}
            {vo2cat ? ` · ${vo2cat}` : ""}
          </SectionTitle>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={rows} {...mouseEvents}>
              {grid}
              {/* Bandas de categoría (ACSM por sexo+edad) que solapan con el rango de datos. */}
              {cuts &&
                [0, ...cuts].map((lo, i) => {
                  const hi = i < cuts.length ? cuts[i] : vmax + 10;
                  if (hi <= vmin || lo >= vmax) return null;
                  return (
                    <ReferenceArea
                      key={i}
                      y1={lo}
                      y2={hi}
                      fill={VO2_COLORS[i]}
                      fillOpacity={0.1}
                      stroke="none"
                      label={{
                        value: t(VO2_NAMES[i]),
                        position: "insideLeft",
                        fill: palette.axis,
                        fontSize: 11,
                      }}
                    />
                  );
                })}
              {mkBand()}
              <XAxis dataKey="date" tickFormatter={dfmt} {...axis} minTickGap={40} />
              <YAxis domain={["dataMin - 1", "dataMax + 1"]} {...axis} />
              <Tooltip {...tipProps(palette)} labelFormatter={dfull} />
              <Line
                type="monotone"
                dataKey="vo2max"
                name={t("h_vo2")}
                stroke={C.vo2}
                dot={false}
                strokeWidth={2.5}
                connectNulls
              />
              {mkBrush()}
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <SectionTitle>{t("sec_h_table")}</SectionTitle>
          <HealthTable rows={range} only={HCOLS_SUMMARY} />
        </Card>
      </div>
    );
  }

  /* ---- SUEÑO ---- */
  if (view === "s_sueno") {
    const data = rows.map((h) => ({
      date: h.date,
      deep: h.deep_seconds != null ? +(h.deep_seconds / 3600).toFixed(2) : null,
      light: h.light_seconds != null ? +(h.light_seconds / 3600).toFixed(2) : null,
      rem: h.rem_seconds != null ? +(h.rem_seconds / 3600).toFixed(2) : null,
      awake: h.awake_seconds != null ? +(h.awake_seconds / 3600).toFixed(2) : null,
      sleep_score: h.sleep_score,
    }));
    const durH = mean(
      range
        .map((h) => h.sleep_seconds)
        .filter((x): x is number => x != null)
        .map((s) => s / 3600),
    );
    // HRV y respiración como media del periodo (coherente con Duración y con el filtro temporal).
    const avgHrv = mean(range.map((h) => h.hrv_avg).filter((x): x is number => x != null));
    const avgResp = mean(range.map((h) => h.respiration).filter((x): x is number => x != null));
    return (
      <div className="space-y-6">
        {clearChip}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Kpi
            label={t("h_sleep_score")}
            value={lastVal(range, "sleep_score") == null ? "—" : String(lastVal(range, "sleep_score"))}
            accent
            sub={t("h_last_night")}
            info={t("info_h_sleep")}
          />
          <Kpi
            label={t("h_sleep_dur")}
            value={durH == null ? "—" : `${fmtNum(durH, 1, loc)} h`}
            accent
            sub={t("h_avg")}
          />
          <Kpi
            label={t("h_hrv")}
            value={avgHrv == null ? "—" : `${fmtNum(avgHrv, 0, loc)} ms`}
            accent
            sub={t("h_avg")}
            info={t("info_h_hrv")}
          />
          <Kpi
            label={t("h_resp")}
            value={avgResp == null ? "—" : `${fmtNum(avgResp, 0, loc)} rpm`}
            accent
            sub={t("h_avg")}
          />
        </div>
        <Card>
          <SectionTitle>{t("sec_h_sleep")}</SectionTitle>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={data} {...mouseEvents}>
              {grid}
              {mkBand("h")}
              <XAxis dataKey="date" tickFormatter={dfmt} {...axis} minTickGap={40} />
              <YAxis yAxisId="h" {...axis} unit="h" />
              <YAxis yAxisId="score" orientation="right" domain={[0, 100]} {...axis} />
              <Tooltip {...tipProps(palette)} labelFormatter={dfull} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="h" dataKey="deep" name={t("st_deep")} stackId="s" fill={C.deep} />
              <Bar yAxisId="h" dataKey="light" name={t("st_light")} stackId="s" fill={C.light} />
              <Bar yAxisId="h" dataKey="rem" name={t("st_rem")} stackId="s" fill={C.rem} />
              <Bar yAxisId="h" dataKey="awake" name={t("st_awake")} stackId="s" fill={C.awake} />
              <Line
                yAxisId="score"
                type="monotone"
                dataKey="sleep_score"
                name={t("h_sleep_score")}
                stroke={C.sleep}
                dot={false}
                strokeWidth={2.5}
                connectNulls
              />
              {mkBrush()}
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <SectionTitle>{t("sec_h_table")}</SectionTitle>
          <HealthTable rows={range} />
        </Card>
      </div>
    );
  }

  /* ---- CRUCE (Readiness / carga) ---- */
  const tsbByDate = new Map(load.map((l) => [l.date, l.tsb]));
  const merged = rows.map((h) => ({
    date: h.date,
    tsb: tsbByDate.has(h.date) ? Math.round((tsbByDate.get(h.date) as number) * 10) / 10 : null,
    hrv_avg: h.hrv_avg,
    readiness: h.readiness,
  }));
  const mergedSel = sel ? merged.slice(sel.start, sel.end + 1) : merged;
  const rTsbHrv = pearson(mergedSel.map((m) => [m.tsb, m.hrv_avg]));
  const rTsbRdy = pearson(mergedSel.map((m) => [m.tsb, m.readiness]));
  const corrTxt = (r: number | null) =>
    r == null
      ? t("h_corr_na")
      : `r = ${fmtNum(r, 2, loc)} (${Math.abs(r) < 0.2 ? t("h_corr_weak") : r > 0 ? t("h_corr_pos") : t("h_corr_neg")})`;

  return (
    <div className="space-y-6">
      {clearChip}
      <Card>
        <SectionTitle>{t("sec_h_cross")}</SectionTitle>
        <p className="mb-3 text-xs text-muted">{t("h_cross_hint")}</p>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={merged} {...mouseEvents}>
            {grid}
            {mkBand("tsb")}
            <XAxis dataKey="date" tickFormatter={dfmt} {...axis} minTickGap={40} />
            <YAxis yAxisId="tsb" {...axis} />
            <YAxis yAxisId="r" orientation="right" domain={[0, 100]} {...axis} />
            <Tooltip {...tipProps(palette)} labelFormatter={dfull} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <ReferenceLine yAxisId="tsb" y={0} stroke={palette.axis} strokeOpacity={0.5} />
            <Line
              yAxisId="tsb"
              type="monotone"
              dataKey="tsb"
              name={t("legend_forma")}
              stroke={C.tsb}
              dot={false}
              strokeWidth={2}
              connectNulls
            />
            <Line
              yAxisId="r"
              type="monotone"
              dataKey="readiness"
              name={t("h_readiness")}
              stroke={C.readiness}
              dot={false}
              strokeWidth={2}
              connectNulls
            />
            <Line
              yAxisId="r"
              type="monotone"
              dataKey="hrv_avg"
              name={t("h_hrv")}
              stroke={C.hrv}
              dot={false}
              strokeWidth={1.5}
              strokeDasharray="3 3"
              connectNulls
            />
            {mkBrush()}
          </ComposedChart>
        </ResponsiveContainer>
        <div className="mt-4 grid gap-2 border-t border-line pt-3 text-sm sm:grid-cols-2">
          <div>
            <span className="text-muted">{t("h_corr_tsb_hrv")}:</span>{" "}
            <span className="font-semibold text-fg">{corrTxt(rTsbHrv)}</span>
          </div>
          <div>
            <span className="text-muted">{t("h_corr_tsb_rdy")}:</span>{" "}
            <span className="font-semibold text-fg">{corrTxt(rTsbRdy)}</span>
          </div>
        </div>
      </Card>
      <Card>
        <SectionTitle>{t("sec_h_hrv")}</SectionTitle>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={rows} {...mouseEvents}>
            {grid}
            {mkBand("hrv")}
            <XAxis dataKey="date" tickFormatter={dfmt} {...axis} minTickGap={40} />
            <YAxis yAxisId="hrv" {...axis} />
            <YAxis yAxisId="rhr" orientation="right" {...axis} />
            <Tooltip {...tipProps(palette)} labelFormatter={dfull} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line
              yAxisId="hrv"
              type="monotone"
              dataKey="hrv_avg"
              name={t("h_hrv")}
              stroke={C.hrv}
              dot={false}
              strokeWidth={2}
              connectNulls
            />
            <Line
              yAxisId="rhr"
              type="monotone"
              dataKey="resting_hr"
              name={t("h_rhr")}
              stroke={C.rhr}
              dot={false}
              strokeWidth={2}
              connectNulls
            />
            {mkBrush()}
          </LineChart>
        </ResponsiveContainer>
      </Card>
      <Card>
        <SectionTitle>{t("sec_h_bb")}</SectionTitle>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={rows} {...mouseEvents}>
            {grid}
            {mkBand("bb")}
            <XAxis dataKey="date" tickFormatter={dfmt} {...axis} minTickGap={40} />
            <YAxis yAxisId="bb" domain={[0, 100]} {...axis} />
            <YAxis yAxisId="stress" orientation="right" domain={[0, 100]} {...axis} />
            <Tooltip {...tipProps(palette)} labelFormatter={dfull} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line
              yAxisId="bb"
              type="monotone"
              dataKey="bb_high"
              name={`${t("h_body_battery")} ↑`}
              stroke={C.bbHigh}
              dot={false}
              strokeWidth={2}
              connectNulls
            />
            <Line
              yAxisId="bb"
              type="monotone"
              dataKey="bb_low"
              name={`${t("h_body_battery")} ↓`}
              stroke={C.bbLow}
              dot={false}
              strokeWidth={2}
              connectNulls
            />
            <Line
              yAxisId="stress"
              type="monotone"
              dataKey="stress_avg"
              name={t("h_stress")}
              stroke={C.stress}
              dot={false}
              strokeWidth={1.5}
              strokeDasharray="3 3"
              connectNulls
            />
            {mkBrush()}
          </ComposedChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}
